import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Stack, Divider, Grid, Select, MenuItem, InputLabel } from "@mui/material";
import Modal, { CommonModalProps } from "..";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DbAssetMarkerInfos } from "../../../Core/Api/Types/db-asset-marker-infos";
import InputTimecode from "../../../Utils/Timecode/Timecode";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { assetService } from "../../../Core/Api/services";
import { produce } from "immer";

import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { RootState, useAppSelector } from "../../../Redux/redux-store";
import { DbMarkerCategory } from "../../../Core/Api/Types/db-marker-category";
import { DbMarkerSeverity } from "../../../Core/Api/Types/db-marker-severity";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ListIcon from "@mui/icons-material/List";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { DbMarkerLevel } from "../../../Core/Api/Types/db-marker-level";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { SecToTimecode, TimecodeToFrame } from "../../../Utils/Timecode/ReactPlayer";
import { useStore } from "react-redux";
import { Store } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { GetTimeInSec } from "../../../Redux/Reducers/redux-slice-react-player";

interface FamilyMarkerDto {
  name: string;
  id: number;
}

interface TypeMarkerDto {
  name: string;
  id: number;
  familyId: number;
}

export type ModalMarkerCreateOrEditElementProps = {
  mode: "create" | "update";
  marker?: DbAssetMarkerInfos;
  tcIn?: string;
  tcOut?: string;
  tcFileIn: string;
  frameRate: number;
  objectId: number;
  families?: FamilyMarkerDto[];
  types?: TypeMarkerDto[];
  onClose?: (marker: DbAssetMarkerInfos) => void;
};

export type ModalMarkerCreateOrEditProps = CommonModalProps<ModalMarkerCreateOrEditElementProps>;

export enum MarkerCategory {
  Human = "Human",
  DubCardImageExtraction = "DubCardImageExtraction",
}

const ModalMarkerCreateOrEdit = React.forwardRef<any, ModalMarkerCreateOrEditProps>((props, ref) => {
  const { mode, tcOut, frameRate, objectId, tcFileIn, closeModal, onClose } = props;
  //   const [tcWhenOpen] = React.useState(() => store.getState().reactPlayer.interface.curentTimecode);
  const tcWhenOpen = SecToTimecode(useAppSelector(GetTimeInSec), frameRate);
  const tcIn = props.tcIn ? props.tcIn : tcWhenOpen;
  const { t } = useTranslation();

  const [marker, setMarker] = useState<DbAssetMarkerInfos>(
    props.marker ?? {
      objectId: objectId,
      familyId: null,
      typeId: null,
      frameIn: tcIn
        ? TimecodeToFrame(tcIn, frameRate) - (tcFileIn !== "" ? TimecodeToFrame(tcFileIn, frameRate) : 0)
        : null,
      frameOut: tcOut
        ? TimecodeToFrame(tcOut, frameRate) - (tcFileIn !== "" ? TimecodeToFrame(tcFileIn, frameRate) : 0)
        : null,
      frameRate: frameRate,
      category: DbMarkerCategory.Human,
      severity: DbMarkerSeverity.INFO,
      level: DbMarkerLevel.Normal,
      comment: null,
      commentReviewer: null,
      platformId: null,
      status: null,
      creationInfos: null,
      thumbnailIn: null,
      thumbnailOut: null,
      updateInfos: null,
      familyInfos: null,
      typeInfos: null,
      timecodeIn: null,
      timecodeOut: null,
      timecodeInFile: tcIn ?? null,
      timecodeOutFile: tcOut ?? null,
      id: 0,
      isNew: true,
    }
  );

  const [markerCategories, setMarkerCategories] = useState<any>(Object.keys(MarkerCategory));
  const [markerFamilies, setMarkerFamilies] = useState<FamilyMarkerDto[]>(props.families ?? []);
  const [markerTypes, setMarkerTypes] = useState<TypeMarkerDto[]>(props.types ?? []);
  const enqueueSnackbar = useCustomSnackbar();
  const formId = "marker-modal";
  const formValid = useAppSelector(GetFormIsValid(formId));

  const tcValid = useMemo((): boolean => {
    if (marker.frameIn === null || marker.frameOut === null) return false;
    if (marker.frameIn > marker.frameOut) return false;

    return true;
  }, [marker, marker.frameIn, marker.frameOut]);

  const handleSave = useCallback(
    (close: boolean) => {
      if (mode === "create")
        assetService.AddMarkerOnObjectId({ objectId: marker.objectId, marker: marker }).then((response) => {
          if (response.success) {
            if (close) closeModal();
            enqueueSnackbar(t("alert:success.markerInserted"), "success");
          } else enqueueSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");

          response.marker!.timecodeInFile = marker.timecodeInFile;
          response.marker!.timecodeOutFile = marker.timecodeOutFile;

          onClose && onClose(response.marker ?? marker);
        });
      else {
        assetService.UpdateMarkerOnObjectId({ objectId: marker.objectId, marker: marker }).then((response) => {
          if (response.success) {
            if (close) closeModal();
            enqueueSnackbar(t("alert:success.markerUpdate"), "success");
          } else enqueueSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");

          response.marker!.timecodeInFile = marker.timecodeInFile;
          response.marker!.timecodeOutFile = marker.timecodeOutFile;

          onClose && onClose(response.marker ?? marker);
        });
      }
    },
    [marker, props.marker, mode]
  );

  const setMarkerTimecode = useCallback(
    (tc: string, where: "in" | "out") => {
      if (where === "in")
        setMarker(
          produce((draft) => {
            draft.timecodeInFile = tc;
            draft.frameIn =
              TimecodeToFrame(tc, frameRate) - (tcFileIn !== "" ? TimecodeToFrame(tcFileIn, frameRate) : 0);

            console.debug("frameIn = " + draft.frameIn);
          })
        );
      else
        setMarker(
          produce((draft) => {
            draft.timecodeOutFile = tc;
            draft.frameOut =
              TimecodeToFrame(tc, frameRate) - (tcFileIn !== "" ? TimecodeToFrame(tcFileIn, frameRate) : 0);

            console.debug("frameOut = " + draft.frameOut);
          })
        );
    },
    [tcFileIn, frameRate]
  );

  useEffect(() => {
    console.debug("modal::marker opened with fps = [" + frameRate + "], tcFileIn = [" + tcFileIn + "]");
  }, []);

  return (
    <Modal
      closeModal={closeModal}
      reference={ref}
      width={650}
      title={
        mode === "create" ? t("modal:title.createMarker", { frameRate }) : t("modal:title.updateMarker", { frameRate })
      }
      footer={
        <Stack direction="row" spacing={2}>
          <Button onClick={() => closeModal()} startIcon={<ArrowBackIcon />} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave(true)}
            disabled={!formValid || !tcValid || (mode === "update" && marker.category !== DbMarkerCategory.Human)}
            startIcon={mode === "create" ? <AddIcon /> : <SyncAltIcon />}
            sx={{ flex: 1 }}
          >
            {mode === "create" ? t("common:create") : t("common:update")}
          </Button>
          {mode === "create" && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSave(false)}
              disabled={!formValid || !tcValid}
              startIcon={<AddIcon />}
              sx={{ flex: 1 }}
            >
              {t("button:createAndKeepOpen")}
            </Button>
          )}
        </Stack>
      }
    >
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
        <InputTimecode
          size="xlarge"
          value={marker.timecodeInFile ?? ""}
          frameRate={frameRate}
          handleKeyDown={() => {}}
          setValue={(tc) => setMarkerTimecode(tc, "in")}
        />
        <KeyboardDoubleArrowRightIcon fontSize="large" />
        <InputTimecode
          size="xlarge"
          value={marker.timecodeOutFile ?? ""}
          frameRate={frameRate}
          handleKeyDown={() => {}}
          setValue={(tc) => setMarkerTimecode(tc, "out")}
        />
      </Stack>

      <Divider sx={{ mt: 3, mb: 3 }} />

      <Grid container spacing={2}>
        <Grid item md={4}>
          <InputLabel sx={{ mb: 0 }}>{t("common:category")}</InputLabel>
          <FormAutocomplete
            fullWidth={true}
            options={markerCategories}
            value={markerCategories.find((x) => x === marker.category)}
            placeholder={t("common:category")}
            onChange={(v: any) =>
              setMarker(
                produce((draft) => {
                  draft.category = v ? v : null;
                  if (v === DbMarkerCategory.DubCardImageExtraction) {
                    draft.familyId = -1;
                    draft.typeId = -1;
                  }
                })
              )
            }
            disabled={mode === "update"}
            validationProps={{
              formId: formId,
              formSectionId: "issue",
              fieldId: "category",
              required: true,
              initialState: false,
            }}
          />
        </Grid>

        <Grid item md={4}>
          <InputLabel sx={{ mb: 0 }}>{t("common:family")}</InputLabel>
          <FormAutocomplete
            fullWidth={true}
            options={markerFamilies}
            value={markerFamilies.find((x) => x.id === marker.familyId)}
            placeholder={t("common:family")}
            onChange={(v: any) =>
              setMarker(
                produce((draft) => {
                  draft.familyId = v ? v.id : null;
                  draft.typeId = null; // Reset type to avoid mismatch with family choice
                })
              )
            }
            disabled={marker.category === DbMarkerCategory.DubCardImageExtraction}
            getOptionLabel={(value: any) => value.name}
            // isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x, s) => x.filter((f) => f.name?.includesAllWords(s.inputValue))}
            validationProps={{
              formId: formId,
              formSectionId: "issue",
              fieldId: "family",
              required: marker.category === DbMarkerCategory.Human ? true : false,
              initialState: false,
            }}
          />
        </Grid>

        <Grid item md={4}>
          <InputLabel sx={{ mb: 0 }}>{t("common:type")}</InputLabel>
          <FormAutocomplete
            fullWidth={true}
            options={!marker.familyId ? [] : markerTypes.filter((x) => x.familyId === marker.familyId)}
            value={markerTypes.find((x) => x.id === marker.typeId)}
            placeholder={t("common:type")}
            onChange={(v: any) =>
              setMarker(
                produce((draft) => {
                  draft.typeId = v ? v.id : null;
                })
              )
            }
            disabled={marker.category === DbMarkerCategory.DubCardImageExtraction}
            getOptionLabel={(value: any) => value.name}
            // isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x, s) => x.filter((f) => f.name?.includesAllWords(s.inputValue))}
            validationProps={{
              formId: formId,
              formSectionId: "issue",
              fieldId: "type",
              required: marker.category === DbMarkerCategory.Human ? true : false,
              initialState: false,
            }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mt: 3, mb: 3 }} />

      <Grid container spacing={2}>
        <Grid item md={6}>
          <InputLabel sx={{ mb: 1 }}>{t("common:severity")}</InputLabel>
          <Select
            startAdornment={<ListIcon />}
            IconComponent={ExpandMoreRoundedIcon}
            value={marker.severity ?? undefined}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            fullWidth
            onChange={(e) =>
              setMarker(
                produce((draft) => {
                  draft.severity = e.target.value !== "" ? DbMarkerSeverity[e.target.value] : null;
                })
              )
            }
          >
            <MenuItem key={t("common:severity")} value={undefined}>
              {t("common:severity")}
            </MenuItem>
            {Object.keys(DbMarkerSeverity).map((value) => (
              <MenuItem key={value} value={DbMarkerSeverity[value]}>
                {DbMarkerSeverity[value]}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item md={6}>
          <InputLabel sx={{ mb: 1 }}>{t("common:level")}</InputLabel>
          <Select
            startAdornment={<ListIcon />}
            IconComponent={ExpandMoreRoundedIcon}
            value={marker.level ?? undefined}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            fullWidth
            onChange={(e) =>
              setMarker(
                produce((draft) => {
                  draft.level = e.target.value !== "" ? DbMarkerLevel[e.target.value] : null;
                })
              )
            }
          >
            <MenuItem key={t("common:level")} value={undefined}>
              {t("common:level")}
            </MenuItem>
            {Object.keys(DbMarkerLevel).map((value) => (
              <MenuItem key={value} value={DbMarkerLevel[value]}>
                {DbMarkerLevel[value]}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      <Divider sx={{ mt: 3, mb: 3 }} />

      <FormInputText
        label={t("common:comment")}
        placeholder={t("field:placeholder.enterMarkerComment")}
        value={marker.comment}
        multiline
        rows={3}
        onChange={(v) => {
          setMarker(
            produce((draft) => {
              draft.comment = v.target.value;
            })
          );
        }}
        validationProps={{
          formId: formId,
          formSectionId: "issue",
          fieldId: "comment",
          initialState: false,
          required: false,
        }}
        disabled={false}
      />
    </Modal>
  );
});

export default ModalMarkerCreateOrEdit;
